/**
 * OcrTest — developer probe for the OCR adapter.
 *
 * Supports multiple files with a concurrent job runner (cap: 3 simultaneous
 * POST /api/ocr/extract calls). Excess jobs sit in `queued` until a slot
 * frees. Uses an inline semaphore backed by `activeRef` — no extra npm deps.
 *
 * The response body carries an `ocr` payload and a `debug` payload (see
 * `OcrDebugSchema` in shared/) — the debug panel on each job surfaces *why*
 * a Claude run fell back to mock and what Claude actually emitted before
 * Zod parsing.
 *
 * Reached by URL only — not linked from HomeScene.
 */
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type ChangeEvent,
  type DragEvent,
} from 'react'
import { Link } from 'react-router-dom'
import { Copy, Loader2, Plus, RefreshCw, Trash2, Upload, X } from 'lucide-react'
import { OcrPreview } from '@/components/OcrPreview'
import {
  OcrExtractResponseSchema,
  type OcrDebug,
  type OcrExtractResponse,
  type OcrResult,
} from '@shared/schemas'
import { ApiError, apiPostMultipart } from '@/lib/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type JobStatus = 'queued' | 'pending' | 'done' | 'error'

type Job = {
  id: string
  file: File
  previewUrl: string
  status: JobStatus
  result?: { ocr: OcrResult; debug: OcrDebug }
  error?: { status: number; message: string }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf'
const ACCEPT_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
const CONCURRENCY = 3

// ---------------------------------------------------------------------------
// Core extract helper (stateless, per-file)
// ---------------------------------------------------------------------------

async function extract(file: File): Promise<OcrExtractResponse> {
  const fd = new FormData()
  fd.append('file', file)
  try {
    const { data } = await apiPostMultipart<OcrExtractResponse>(
      '/api/ocr/extract',
      fd,
      OcrExtractResponseSchema,
    )
    return data
  } catch (err) {
    const apiErr = err instanceof ApiError ? err : null
    throw {
      status: apiErr?.status ?? 0,
      message:
        apiErr?.message ?? (err instanceof Error ? err.message : 'unknown'),
    } satisfies { status: number; message: string }
  }
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`
  return `${(n / (1024 * 1024)).toFixed(2)} MiB`
}

function makeJob(file: File): Job {
  return {
    id: crypto.randomUUID(),
    file,
    previewUrl: URL.createObjectURL(file),
    status: 'queued',
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProviderBadge({ debug }: { debug: OcrDebug }) {
  // Show the *effective* provider — that's what produced the JSON below.
  // When Claude was attempted but fell back, flag it so a dev doesn't
  // mistake fixture data for a real extract.
  const fellBack =
    debug.attempted_provider === 'claude' &&
    debug.effective_provider === 'mock'
  const isClaude = debug.effective_provider === 'claude'
  return (
    <span
      className={`text-xs font-mono uppercase tracking-wide rounded-full px-2 py-0.5 ${
        isClaude
          ? 'bg-emerald-100 text-emerald-800'
          : fellBack
            ? 'bg-amber-100 text-amber-800'
            : 'bg-slate-200 text-slate-700'
      }`}
    >
      {fellBack
        ? `claude → mock (${debug.fallback_reason ?? 'unknown'})`
        : `provider: ${debug.effective_provider}`}
    </span>
  )
}

function StatusBadge({ status }: { status: JobStatus }) {
  const cfg: Record<JobStatus, { label: string; cls: string }> = {
    queued: { label: 'Queued', cls: 'bg-slate-100 text-slate-600' },
    pending: { label: 'Processing…', cls: 'bg-blue-100 text-blue-700' },
    done: { label: 'Done', cls: 'bg-emerald-100 text-emerald-800' },
    error: { label: 'Error', cls: 'bg-rose-100 text-rose-700' },
  }
  const { label, cls } = cfg[status]
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 ${cls}`}>
      {status === 'pending' && <Loader2 className="h-3 w-3 animate-spin" />}
      {label}
    </span>
  )
}

function DebugPanel({ debug }: { debug: OcrDebug }) {
  const fellBack =
    debug.attempted_provider === 'claude' &&
    debug.effective_provider === 'mock'
  return (
    <section
      className={`rounded-2xl border px-4 py-3 ${
        fellBack
          ? 'border-amber-300 bg-amber-50'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wide text-slate-500">
          Debug
        </h3>
        <span className="text-xs text-slate-400 font-mono">
          {debug.duration_ms} ms
        </span>
      </div>
      {fellBack && (
        <p className="text-sm text-amber-900 mb-3">
          Claude run failed — JSON below is a <strong>mock fixture</strong>,
          not extracted from your image. Reason:{' '}
          <code className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">
            {debug.fallback_reason ?? 'unknown'}
          </code>
        </p>
      )}
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-xs">
        <DebugField label="attempted" value={debug.attempted_provider} />
        <DebugField label="effective" value={debug.effective_provider} />
        {debug.fallback_reason && (
          <DebugField label="reason" value={debug.fallback_reason} />
        )}
        {debug.file && (
          <DebugField
            label="file"
            value={`${formatBytes(debug.file.bytes)} ${debug.file.mime_type ?? ''}`.trim()}
          />
        )}
      </dl>
      {debug.api_error && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
            Anthropic error
          </summary>
          <pre className="mt-2 rounded-xl bg-slate-900 text-slate-100 p-3 overflow-auto font-mono leading-relaxed">
            {JSON.stringify(debug.api_error, null, 2)}
          </pre>
        </details>
      )}
      {debug.zod_issues && debug.zod_issues.length > 0 && (
        <details className="mt-3 text-xs" open>
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
            Zod schema mismatch ({debug.zod_issues.length} issue
            {debug.zod_issues.length === 1 ? '' : 's'})
          </summary>
          <ul className="mt-2 space-y-1.5 text-slate-700">
            {debug.zod_issues.map((iss, i) => (
              <li key={i} className="font-mono">
                <span className="text-rose-700">{iss.path.join('.') || '(root)'}</span>
                <span className="text-slate-400"> — </span>
                <span>{iss.message}</span>
                <span className="text-slate-400"> [{iss.code}]</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {debug.raw_tool_input !== undefined && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
            Raw Claude output (pre-normalisation)
          </summary>
          <pre className="mt-2 rounded-xl bg-slate-900 text-slate-100 p-3 overflow-auto max-h-72 font-mono leading-relaxed">
            {JSON.stringify(debug.raw_tool_input, null, 2)}
          </pre>
        </details>
      )}
    </section>
  )
}

function DebugField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-slate-400 uppercase tracking-wide text-[10px]">
        {label}
      </dt>
      <dd className="font-mono text-slate-800 truncate">{value}</dd>
    </div>
  )
}

function JobTile({
  job,
  onRetry,
  onRemove,
}: {
  job: Job
  onRetry: (id: string) => void
  onRemove: (id: string) => void
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
      {/* Thumbnail */}
      <div className="relative w-full h-40 bg-slate-100 flex-shrink-0">
        <img
          src={job.previewUrl}
          alt={job.file.name}
          className="w-full h-full object-cover"
        />
        {/* Status overlay for pending */}
        {job.status === 'pending' && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-white animate-spin" />
          </div>
        )}
      </div>

      {/* Info row */}
      <div className="px-3 pt-3 pb-2 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate" title={job.file.name}>
              {job.file.name}
            </p>
            <p className="text-xs text-slate-400">{humanSize(job.file.size)}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <StatusBadge status={job.status} />
            {job.status === 'done' && job.result && (
              <ProviderBadge debug={job.result.debug} />
            )}
          </div>
        </div>

        {/* Error message */}
        {job.status === 'error' && job.error && (
          <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 text-xs text-rose-700">
            <strong className="font-semibold">{job.error.status > 0 ? job.error.status : 'Error'}</strong>{' '}
            {job.error.message}
          </div>
        )}

        {/* Done: collapsible details */}
        {job.status === 'done' && job.result && (
          <details className="text-xs">
            <summary className="cursor-pointer select-none text-slate-500 hover:text-slate-800 py-1">
              Show extracted data
            </summary>
            <div className="mt-2 flex flex-col gap-3">
              <DebugPanel debug={job.result.debug} />
              <OcrPreview ocr={job.result.ocr} />
              <JsonPanel ocr={job.result.ocr} />
            </div>
          </details>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-auto pt-1">
          <button
            onClick={() => onRetry(job.id)}
            disabled={job.status === 'pending'}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
          <button
            onClick={() => onRemove(job.id)}
            disabled={job.status === 'pending'}
            className="flex items-center gap-1 text-xs text-rose-400 hover:text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ml-auto"
          >
            <X className="h-3.5 w-3.5" />
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}

function JsonPanel({ ocr }: { ocr: OcrResult }) {
  const [copied, setCopied] = useState(false)
  const json = JSON.stringify(ocr, null, 2)
  const onCopy = async () => {
    await navigator.clipboard.writeText(json)
    setCopied(true)
    setTimeout(() => setCopied(false), 1200)
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Raw JSON</span>
        <button
          onClick={onCopy}
          className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-700 transition-colors"
        >
          <Copy className="h-3 w-3" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="rounded-xl bg-slate-900 text-slate-100 text-[10px] leading-relaxed p-3 overflow-auto max-h-60 font-mono">
        {json}
      </pre>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function OcrTest() {
  const [jobs, setJobs] = useState<Job[]>([])
  const activeRef = useRef(0)

  // Inline semaphore: process up to CONCURRENCY queued jobs at once
  const processQueue = useCallback((currentJobs: Job[]) => {
    const available = CONCURRENCY - activeRef.current
    if (available <= 0) return

    const queued = currentJobs.filter((j) => j.status === 'queued')
    const toRun = queued.slice(0, available)
    if (toRun.length === 0) return

    const ids = new Set(toRun.map((j) => j.id))

    // Transition selected jobs to pending
    setJobs((prev) =>
      prev.map((j) => (ids.has(j.id) ? { ...j, status: 'pending' as const } : j)),
    )

    activeRef.current += toRun.length

    for (const job of toRun) {
      extract(job.file)
        .then((result) => {
          setJobs((prev) =>
            prev.map((j) =>
              j.id === job.id ? { ...j, status: 'done' as const, result } : j,
            ),
          )
        })
        .catch((err: { status: number; message: string }) => {
          setJobs((prev) =>
            prev.map((j) =>
              j.id === job.id ? { ...j, status: 'error' as const, error: err } : j,
            ),
          )
        })
        .finally(() => {
          activeRef.current -= 1
          // Trigger queue processing after each completion
          setJobs((prev) => {
            // Return same array reference to trigger the effect; the effect
            // calls processQueue with the latest snapshot.
            return [...prev]
          })
        })
    }
  }, [])

  // Trigger processQueue whenever jobs array changes
  useEffect(() => {
    processQueue(jobs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs])

  // Revoke all object URLs on unmount
  useEffect(() => {
    return () => {
      setJobs((prev) => {
        prev.forEach((j) => URL.revokeObjectURL(j.previewUrl))
        return prev
      })
    }
  }, [])

  const addFiles = useCallback((files: File[]) => {
    const newJobs = files.map(makeJob)
    setJobs((prev) => [...prev, ...newJobs])
  }, [])

  const retryJob = useCallback((id: string) => {
    setJobs((prev) =>
      prev.map((j) =>
        j.id === id ? { ...j, status: 'queued' as const, result: undefined, error: undefined } : j,
      ),
    )
  }, [])

  const removeJob = useCallback((id: string) => {
    setJobs((prev) => {
      const job = prev.find((j) => j.id === id)
      if (job) URL.revokeObjectURL(job.previewUrl)
      return prev.filter((j) => j.id !== id)
    })
  }, [])

  const clearAll = useCallback(() => {
    setJobs((prev) => {
      prev.forEach((j) => URL.revokeObjectURL(j.previewUrl))
      return []
    })
  }, [])

  const copyAllJson = useCallback(async () => {
    const doneJobs = jobs.filter((j) => j.status === 'done' && j.result)
    const payload = doneJobs.map((j) => ({
      file: j.file.name,
      provider: j.result!.debug.effective_provider,
      ocr: j.result!.ocr,
      debug: j.result!.debug,
    }))
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
  }, [jobs])

  const hasDoneResults = jobs.some((j) => j.status === 'done')

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900 px-6 py-10">
      <div className="max-w-5xl mx-auto flex flex-col gap-6">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">OCR test</h1>
            <p className="text-sm text-slate-500 max-w-xl">
              Drop contract photos. Up to {CONCURRENCY} files are processed concurrently.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {jobs.length > 0 && (
              <button
                onClick={() => document.getElementById('drop-zone-input')?.click()}
                className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add more
              </button>
            )}
            {hasDoneResults && (
              <button
                onClick={copyAllJson}
                className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm transition-colors"
              >
                <Copy className="h-4 w-4" />
                Copy all JSON
              </button>
            )}
            {jobs.length > 0 && (
              <button
                onClick={clearAll}
                className="flex items-center gap-1.5 text-sm text-rose-500 hover:text-rose-700 bg-white border border-rose-200 rounded-xl px-3 py-1.5 shadow-sm transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Clear all
              </button>
            )}
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 transition-colors">
              ← Zpět
            </Link>
          </div>
        </header>

        {/* Drop zone — always visible */}
        <DropZoneWithId onFiles={addFiles} />

        {/* Job grid */}
        {jobs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job) => (
              <JobTile key={job.id} job={job} onRetry={retryJob} onRemove={removeJob} />
            ))}
          </div>
        )}
      </div>
    </main>
  )
}

// Wrapper that exposes the hidden input via a stable DOM id so the
// "Add more" button in the header can trigger it.
function DropZoneWithId({ onFiles }: { onFiles: (files: File[]) => void }) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const pickImages = (fileList: FileList | null | undefined): File[] => {
    if (!fileList) return []
    return Array.from(fileList).filter((f) => ACCEPT_MIME.includes(f.type))
  }

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = pickImages(e.target.files)
    if (files.length) onFiles(files)
    e.target.value = ''
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const files: File[] = []
    for (const item of Array.from(e.dataTransfer.items)) {
      if (item.kind === 'file' && ACCEPT_MIME.includes(item.type)) {
        const f = item.getAsFile()
        if (f) files.push(f)
      }
    }
    if (files.length) onFiles(files)
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`rounded-2xl border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${
        dragOver
          ? 'border-emerald-500 bg-emerald-50'
          : 'border-slate-300 bg-white hover:border-slate-400'
      }`}
    >
      <div className="flex flex-col items-center gap-2 text-slate-500">
        <Upload className="h-6 w-6 text-slate-400" />
        <p className="text-sm">
          Drop JPEG / PNG / WebP / GIF files here, or{' '}
          <span className="text-emerald-600 font-medium">click to pick</span>
        </p>
        <p className="text-xs text-slate-400">Multiple files supported</p>
      </div>
      <input
        id="drop-zone-input"
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={handleInput}
      />
    </div>
  )
}
